import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { testimonials } from "../content";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 },
  }),
};

export const Testimonials = () => {
  const { ratingValue, ratingMax, ratingCount, items } = testimonials;

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="bg-bone py-24 md:py-32 border-t border-charcoal/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-12 gap-y-10 md:gap-x-10 items-end mb-16 md:mb-20"
        >
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-10 h-px bg-taupe" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-taupe">
                Trusted by homeowners & founders
              </span>
            </div>
            <h2
              data-testid="testimonials-headline"
              className="font-serif text-charcoal text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em]"
            >
              {ratingValue}/{ratingMax} Stars.<br />
              <span className="text-taupe italic">Quietly earned.</span>
            </h2>
          </div>

          <div className="col-span-12 md:col-span-5 md:col-start-8">
            <div
              data-testid="rating-block"
              className="flex md:justify-end items-center gap-3 text-charcoal"
            >
              <div className="flex gap-1" aria-hidden>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={18}
                    strokeWidth={1.25}
                    className="fill-charcoal text-charcoal"
                  />
                ))}
              </div>
              <span className="font-serif text-2xl text-charcoal">
                {ratingValue}
              </span>
              <span className="text-sm text-charcoal/60">
                · {ratingCount} verified reviews
              </span>
            </div>
            <p className="mt-3 md:text-right text-sm text-charcoal/60">
              Across Google, Houzz & Architectural Digest
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 md:gap-x-12 gap-y-14">
          {items.map((t, i) => (
            <motion.figure
              key={i}
              data-testid={`testimonial-${i}`}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              className="relative"
            >
              <span
                aria-hidden
                className="absolute -top-6 -left-1 font-serif italic text-taupe/40 text-7xl leading-none select-none"
              >
                “
              </span>
              <blockquote className="font-serif text-charcoal text-xl md:text-2xl leading-[1.45] tracking-[-0.01em] pl-1">
                {t.quote}
              </blockquote>
              <figcaption className="mt-8 pl-1">
                <div className="block w-10 h-px bg-taupe mb-4" />
                <p className="text-sm text-charcoal">{t.author}</p>
                <p className="text-[12px] text-charcoal/60 mt-1">{t.role}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
